import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AdminRoute from './components/AdminRoute';
// ChatWidget removed in favor of ChatSidebar
import ConsoleCapture from './components/ConsoleCapture/ConsoleCapture.jsx';
import ErrorBoundaryWithRecovery from './components/ErrorBoundaryWithRecovery';
import FloatingLeoWidget from './components/FloatingLeoWidget';
import { NotificationProvider, useNotify } from './components/NotificationProvider';
import PageLayout from './components/PageLayout/PageLayout';
import PerformanceMonitor from './components/PerformanceMonitor';
import RouteFallback from './components/RouteFallback';
import SessionActivityTracker from './components/SessionActivityTracker';
import { DataRefreshProvider } from './contexts/DataRefreshContext';
import useUserAuth from './hooks/useUserAuth';
import { initRoutePrefetch } from './utils/prefetchRoutes';
import scriptManager from './utils/scriptManager';
import sessionManager from './utils/sessionManager';
import shieldcrmLogo from './assets/shieldcrm-logo.svg';
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Route load error', error, info);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', info.componentStack);
  }
  render() {
    if (this.state.error) {
      // Modal confirmation state
      if (!this.state.showModal) this.state.showModal = false;
      // Modal for confirmation (replaces alert)
      const Modal = ({ open, onClose, children }) =>
        open ? (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.25)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                padding: 32,
                minWidth: 320,
                maxWidth: 420,
              }}
            >
              {children}
              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <button
                  style={{
                    padding: '8px 20px',
                    fontSize: 16,
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  onClick={onClose}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        ) : null;

      // Branding
      const logo = shieldcrmLogo;

      // ConsoleCapture with modal confirmation
      // Patch ConsoleCapture to use modal
      const PatchedConsoleCapture = props => {
        const [modalOpen, setModalOpen] = React.useState(false);
        const [modalMsg, setModalMsg] = React.useState('');
        return (
          <>
            <ConsoleCapture
              {...props}
              onCopy={msg => {
                setModalMsg(msg);
                setModalOpen(true);
              }}
            />
            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
              <div style={{ fontSize: 18, color: '#1976d2', marginBottom: 12 }}>
                Console Output Copied
              </div>
              <div style={{ fontSize: 15, color: '#333' }}>
                {modalMsg || 'Console output has been copied to your clipboard.'}
              </div>
            </Modal>
          </>
        );
      };

      return (
        <div
          style={{
            padding: 32,
            maxWidth: 600,
            margin: '0 auto',
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <img src={logo} alt='ShieldCRM Logo' style={{ height: 40 }} />
            <span style={{ fontSize: 28, fontWeight: 700, color: '#0B3A50', letterSpacing: 1 }}>
              ShieldCRM
            </span>
          </div>
          <h2 style={{ color: '#d32f2f', marginBottom: 16 }}>Page Load Error</h2>
          <p style={{ fontSize: 18, marginBottom: 12 }}>
            There were errors on this page which prevent it from loading correctly.
            <br />
            Please contact your administrator and share the details below.
          </p>
          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1976d2' }}>
              Show Console Output
            </summary>
            <textarea
              style={{
                width: '100%',
                minHeight: 120,
                marginTop: 8,
                fontFamily: 'monospace',
                fontSize: 14,
                background: '#fafafa',
                border: '1px solid #ccc',
                borderRadius: 4,
                padding: 8,
              }}
              readOnly
              value={
                window.consoleCapture?.logs
                  ?.map(log => `[${log.timestamp}] ${log.type}: ${log.message}`)
                  .join('\n') || 'No console output captured.'
              }
            />
          </details>
          <div style={{ marginBottom: 24 }}>
            <button
              style={{
                padding: '8px 20px',
                fontSize: 16,
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              onClick={() => this.setState({ error: null })}
            >
              Retry
            </button>
          </div>
          {/* Load ConsoleCapture for error export/copy with modal confirmation */}
          <PatchedConsoleCapture />
        </div>
      );
    }
    return this.props.children;
  }
}
// Lazily loaded route components for code splitting
const Admin = lazy(() => import('./pages/Admin/Admin'));
const RawRecords = lazy(() => import('./pages/Admin/RawRecords'));
const UserRegistry = lazy(() => import('./pages/Admin/UserRegistry'));
const TrashDashboard = lazy(() => import('./pages/Admin/TrashDashboard'));
const FieldManager = lazy(() => import('./pages/Admin/FieldManager'));
const CompanyProfile = lazy(() => import('./pages/Admin/CompanyProfile'));
const TemplatesManager = lazy(() => import('./pages/Admin/Templates/TemplatesManager'));
const EmailTemplatesManager = lazy(
  () => import('./pages/Admin/EmailTemplates/EmailTemplatesManager')
);
const AssociationNameVerification = lazy(() => import('./pages/Admin/AssociationNameVerification'));
const AssociationNameCleanup = lazy(() => import('./pages/Admin/AssociationNameCleanup'));
const FileSummarizer = lazy(() => import('./pages/Admin/FileSummarizer'));
const FeedbackManager = lazy(() => import('./pages/Admin/FeedbackManager'));
const Duplicates = lazy(() => import('./pages/Associations/Duplicates'));
const ProgressBar = lazy(() => import('./pages/Associations/ProgressBar'));
const AssociationForm = lazy(() => import('./pages/Associations/Form'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

// local imports
import Associations from './pages/Associations/Associations';
const PipelineBoard = lazy(() => import('./components/PipelineBoard'));
const OpenAssociation = lazy(() => import('./pages/OpenAssociation/OpenAssociation'));
const Reporting = lazy(() => import('./pages/Reporting/Reporting'));
const SalesDashboard = lazy(() => import('./pages/Reporting/SalesDashboard'));
const LeadBuyingInsights = lazy(() => import('./pages/Reporting/LeadBuyingInsights'));
const DealSetup = lazy(() => import('./pages/DealSetup/DealSetup'));
const DownloadBuildiumData = lazy(() => import('./pages/OpenAssociation/DownloadBuildiumData'));
const ActionItems = lazy(() => import('./pages/ActionItems/ActionItems'));
const DebugPage = lazy(() => import('./pages/DebugPage'));

// mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from './contexts/ThemeContext';

const LoginRedirect = () => {
  const { instance, inProgress, accounts } = useMsal();
  const navigate = useNavigate();
  const { showMessage } = useNotify();

  const signIn = async () => {
    try {
      // Store the current page before login to redirect back after authentication
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/') {
        localStorage.setItem('lastVisitedPage', currentPath);
      }

      // instance.initialize() is handled by MsalProvider
      await instance.loginRedirect({
        redirectUri: window.location.origin, // Always redirect to base URL
      });
    } catch (err) {
      console.error('Error signing in:', err);
      showMessage('Error signing in: ' + err.message, { severity: 'error', persist: true });
    }
  };

  useEffect(() => {
    // In development mode, auto-bypass authentication
    if (import.meta.env.DEV) {
      console.log('🚀 Development mode detected - bypassing authentication');
      const lastVisitedPage = localStorage.getItem('lastVisitedPage') || '/associations';
      localStorage.removeItem('lastVisitedPage');
      navigate(lastVisitedPage);
      return;
    }

    if (accounts.length > 0 && window.location.pathname === '/') {
      // Only redirect if user is at root path (just completed login)
      const lastVisitedPage = localStorage.getItem('lastVisitedPage') || '/associations';
      localStorage.removeItem('lastVisitedPage'); // Clean up
      navigate(lastVisitedPage);
    }
  }, [accounts, navigate]);

  if (inProgress !== InteractionStatus.None) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,.12)',
        }}
      >
        <Box sx={{ textAlign: 'center', bgcolor: 'white', p: 4, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Processing Login...</Typography>
          <Typography variant="body2" color="text.secondary">Please wait while we complete authentication.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(0,0,0,.12)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          width: 300,
          py: 5,
          borderRadius: 2,
          boxShadow: 2,
          textAlign: 'center',
          maxWidth: '90vw',
        }}
      >
        {/*           <img style={{ width: "200px" }} src={logo} alt="" />
         */}{' '}
        <Typography variant='h6' sx={{ fontWeight: 600, mb: 3, mt: 2 }}>
          {import.meta.env.DEV ? 'Development Mode - Auto Login' : 'You are signed out'}
        </Typography>
        {import.meta.env.DEV && (
          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            Authentication bypassed in development mode
            <br />
            Signed in as: Mario Vaccari
          </Typography>
        )}
        <Button
          sx={{ color: 'white' }}
          variant='contained'
          disableElevation
          onClick={signIn}
          disabled={import.meta.env.DEV}
        >
          {import.meta.env.DEV ? 'Redirecting...' : 'Sign In'}
        </Button>
      </Box>
    </Box>
  );
};

function App() {
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();

  // Initialize user authentication and auto-registration (only once per app lifecycle)
  useUserAuth();

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('accounts', accounts);
      console.log(window.location.pathname);
      console.log('redirect', window.location.pathname !== '/');
    }
  }, [accounts]);

  useEffect(() => {
    // Initialize script manager for password prevention and analytics
    scriptManager.initialize();
    // Initialize lightweight route prefetch listeners
    initRoutePrefetch();

    // In development mode, bypass authentication check
    if (import.meta.env.DEV) {
      console.log('🚀 Development mode detected - skipping authentication checks');
      return;
    }

    if (accounts.length > 0) {
      // User is authenticated with MSAL, check session validity
      const isValidSession = sessionManager.isSessionValid();
      if (!isValidSession) {
        console.log('🚪 Session expired - redirecting to login');
        // Clear MSAL cache and redirect to login
        instance.logout({
          postLogoutRedirectUri: window.location.origin,
        });
        return;
      }

      // Session is valid, update activity
      sessionManager.updateActivity();
      /*         if (adminEmails.includes(accounts[0].username?.toLowerCase())) {
          setAdmin(true);
        }

        if (testingEmails.includes(accounts[0].username?.toLowerCase())) {
          setDisplayDemo(true);
        } */
    } else if (accounts.length === 0 && window.location.pathname !== '/') {
      // User is not authenticated and trying to access a protected route
      // Store the current path and redirect to login
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('lastVisitedPage', currentPath);
      navigate('/');
    }
  }, [accounts, navigate]);

  return (
    <NotificationProvider>
      <ThemeProvider>
        <DataRefreshProvider>
          <SessionActivityTracker>
            <PerformanceMonitor />
            <ErrorBoundaryWithRecovery componentName='App'>
              <Suspense fallback={<RouteFallback label='Loading page...' />}>
                <Routes>
                  <Route exact path='/' element={<LoginRedirect />} />
                  <Route
                    exact
                    path='/associations'
                    element={
                      <PageLayout>
                        <Associations />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/pipeline'
                    element={
                      <PageLayout>
                        <PipelineBoard />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/deal-setup'
                    element={
                      <PageLayout>
                        <DealSetup />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/associations/:id'
                    element={
                      <PageLayout>
                        <OpenAssociation />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/debug'
                    element={
                      <PageLayout>
                        <DebugPage />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/reporting'
                    element={
                      <PageLayout>
                        <Reporting />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/reporting/sales-dashboard'
                    element={
                      <PageLayout>
                        <SalesDashboard />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/reporting/lead-buying-insights'
                    element={
                      <PageLayout>
                        <LeadBuyingInsights />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/action-items'
                    element={
                      <PageLayout>
                        <ActionItems />
                      </PageLayout>
                    }
                  />

                  <Route
                    exact
                    path='/download'
                    element={
                      <PageLayout>
                        <DownloadBuildiumData />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <Admin />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/file-summarizer'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <FileSummarizer />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/feedback'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <FeedbackManager />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/user-registry'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <UserRegistry />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/raw-records'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <RawRecords />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/trash'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <TrashDashboard />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/records'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <RawRecords />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/field-manager'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <FieldManager />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/company-profile'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <CompanyProfile />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/templates'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <TemplatesManager />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/email-templates'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <EmailTemplatesManager />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/name-verification'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <AssociationNameVerification />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/admin/association-cleanup'
                    element={
                      <PageLayout>
                        <AdminRoute>
                          <AssociationNameCleanup />
                        </AdminRoute>
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/associations/duplicates'
                    element={
                      <PageLayout>
                        <Duplicates />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/associations/progress-bar'
                    element={
                      <PageLayout>
                        <ProgressBar />
                      </PageLayout>
                    }
                  />
                  <Route
                    exact
                    path='/associations/form'
                    element={
                      <PageLayout>
                        <AssociationForm />
                      </PageLayout>
                    }
                  />
                  {/* Catch-all route for 404 - must be last */}
                  <Route
                    path='*'
                    element={
                      <PageLayout>
                        <NotFound />
                      </PageLayout>
                    }
                  />
                </Routes>
              </Suspense>
            </ErrorBoundaryWithRecovery>
            {/* Floating Leo widget - lower-left corner, hidden on admin pages */}
            <FloatingLeoWidget />
          </SessionActivityTracker>
        </DataRefreshProvider>
      </ThemeProvider>
    </NotificationProvider>
  );
}

export default App;
